import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Table, Tag, Space, Button, Card, Input, DatePicker, Select, Row, Col,
    Typography, Tooltip, message, Modal, Image,
} from 'antd';
import {
    SearchOutlined, ReloadOutlined, EyeOutlined, FileImageOutlined,
    CheckCircleOutlined, ExclamationCircleOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../../hooks/useAuth';
import { fuelFormImageApi } from './api';
import FuelFormImageUploader from './FuelFormImageUploader';
import FuelFormImageReviewDrawer from './FuelFormImageReviewDrawer';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// 用户可见状态收敛为 5 档；后端 status 参数支持逗号分隔多值与 pending_review_all 合并值
const STATUS_FILTER_OPTIONS = [
    { value: 'pending_review_all', label: '待审核' },
    { value: 'pending_extract,pending_match', label: '识别中' },
    { value: 'confirmed', label: '已确认' },
    { value: 'rejected', label: '已拒绝' },
    { value: 'failed_permanent', label: '识别失败' },
];

// 内部 8 种细分状态 → 5 档展示；待审核的两种特殊情况降级为附注小标签
const STATUS_DISPLAY = {
    pending_extract: { color: 'blue', label: '识别中' },
    pending_match: { color: 'blue', label: '识别中' },
    pending_review: { color: 'gold', label: '待审核' },
    pending_review_low_confidence: {
        color: 'gold', label: '待审核',
        hint: { color: 'orange', label: '置信度低', tip: 'AI 对识别结果把握不足，审核时请逐项核对' },
    },
    pending_review_ambiguous_match: {
        color: 'gold', label: '待审核',
        hint: { color: 'volcano', label: '多候选', tip: '自动匹配到多个可能的航班，需人工挑选' },
    },
    failed_permanent: { color: 'red', label: '识别失败' },
    confirmed: { color: 'green', label: '已确认' },
    rejected: { color: 'default', label: '已拒绝' },
};

// 加油单识别航班勾稽：航班匹配状态标签
const MATCH_STATUS_TAG = {
    auto_matched: { color: 'green', label: '自动匹配' },
    manual_matched: { color: 'cyan', label: '人工选定' },
    unmatched: { color: 'default', label: '未匹配' },
};


// 外部抓取自动接入：来源标签（S4.1 人工上传=默认色 / 自动抓取=蓝色）
const SOURCE_TYPE_OPTIONS = [
    { value: 'manual_upload', label: '人工上传', color: 'default' },
    { value: 'external_crawl', label: '自动抓取', color: 'blue' },
];
const SOURCE_TYPE_COLOR = SOURCE_TYPE_OPTIONS.reduce((acc, o) => { acc[o.value] = o.color; return acc; }, {});
const SOURCE_TYPE_LABEL = SOURCE_TYPE_OPTIONS.reduce((acc, o) => { acc[o.value] = o.label; return acc; }, {});

const SourceTag = ({ sourceType }) => (
    <Tag color={SOURCE_TYPE_COLOR[sourceType] || 'default'}>
        {SOURCE_TYPE_LABEL[sourceType] || sourceType || '人工上传'}
    </Tag>
);

const StatusTag = ({ status }) => {
    const display = STATUS_DISPLAY[status] || { color: 'default', label: status };
    return (
        <Space size={4}>
            <Tag color={display.color} style={{ margin: 0 }}>{display.label}</Tag>
            {display.hint && (
                <Tooltip title={display.hint.tip}>
                    <Tag color={display.hint.color} style={{ margin: 0 }}>{display.hint.label}</Tag>
                </Tooltip>
            )}
        </Space>
    );
};

const ConfidenceTag = ({ value, status }) => {
    if (value == null) return <Text type="secondary">-</Text>;
    const pct = (value * 100).toFixed(0);
    const color = status === 'pending_review_low_confidence' ? 'orange'
        : value > 0.85 ? 'green'
            : value > 0.7 ? 'gold' : 'orange';
    return <Tag color={color}>{pct}%</Tag>;
};

const PreviewThumb = ({ imageId }) => {
    const [url, setUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const handleOpen = async () => {
        if (url) return;
        setLoading(true);
        try {
            const res = await fuelFormImageApi.signedUrl(imageId);
            setUrl((res.data || res)?.signed_url);
        } catch {
            message.error('无法加载图片');
        } finally { setLoading(false); }
    };
    return (
        <Tooltip title="点击预览原图">
            <Button
                type="link"
                icon={<FileImageOutlined />}
                size="small"
                loading={loading}
                onClick={handleOpen}
            />
            {url && (
                <Image src={url} alt="preview" width={0} height={0} style={{ display: 'none' }}
                    preview={{ visible: true, onVisibleChange: (v) => { if (!v) setUrl(null); } }} />
            )}
        </Tooltip>
    );
};

/**
 * FuelFormImageList — 加油单图片识别管理列表。
 *
 * 包含：上传入口 + 筛选 + 列表 + 详情/审核 Drawer 入口。
 */
const FuelFormImageList = () => {
    const { hasPermission } = useAuth();
    const hasEditPermission = hasPermission('statistics:fuel:edit');

    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        status: 'pending_review_all',
        flight_number: '',
        aircraft_reg: '',
        dateRange: null,
        source_type: undefined,
    });
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
    const filtersRef = useRef(filters);
    const paginationRef = useRef(pagination);
    filtersRef.current = filters;
    paginationRef.current = pagination;

    const [reviewImageId, setReviewImageId] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    // S5.2: 抓取页标签跳转带 detail_id 参数 → 直接打开该识别记录的审核 Drawer。
    // 关闭 Drawer 时把参数从 URL 摘掉，避免刷新后反复弹出。
    const [searchParams, setSearchParams] = useSearchParams();
    const detailIdFromUrl = searchParams.get('detail_id');
    useEffect(() => {
        if (detailIdFromUrl) setReviewImageId(detailIdFromUrl);
    }, [detailIdFromUrl]);
    const clearDetailIdParam = () => {
        if (!searchParams.get('detail_id')) return;
        const next = new URLSearchParams(searchParams);
        next.delete('detail_id');
        setSearchParams(next, { replace: true });
    };

    const fetchData = useCallback(async (pg, flt) => {
        const page = pg || paginationRef.current;
        const filter = flt || filtersRef.current;
        setLoading(true);
        try {
            const params = {
                page: page.current,
                page_size: page.pageSize,
            };
            if (filter.status) params.status = filter.status;
            if (filter.source_type) params.source_type = filter.source_type;
            if (filter.flight_number) params.flight_number = filter.flight_number.trim();
            if (filter.aircraft_reg) params.aircraft_reg = filter.aircraft_reg.trim();
            if (filter.dateRange?.[0]) params.flight_date_start = filter.dateRange[0].format('YYYY-MM-DD');
            if (filter.dateRange?.[1]) params.flight_date_end = filter.dateRange[1].format('YYYY-MM-DD');
            const res = await fuelFormImageApi.list(params);
            const result = res.data || res;
            const items = result.items || [];
            const nextTotal = result.total || 0;
            if (page.current > 1 && items.length === 0 && nextTotal > 0) {
                const lastPage = Math.max(1, Math.ceil(nextTotal / page.pageSize));
                if (lastPage !== page.current) {
                    const clamped = { ...page, current: lastPage };
                    paginationRef.current = clamped;
                    setPagination(clamped);
                    await fetchData(clamped, filter);
                    return;
                }
            }
            setData(items);
            setTotal(nextTotal);
        } catch (err) {
            message.error('加载失败: ' + (err.response?.data?.detail?.message || err.message));
        } finally {
            setLoading(false);
        }
    }, []);

    const reloadList = useCallback(() => {
        fetchData(paginationRef.current, filtersRef.current);
    }, [fetchData]);

    useEffect(() => { reloadList(); }, [reloadList]);

    // setState 是异步的：改筛选后立刻查询时必须把 nextFilters / nextPg 显式传进 fetchData，
    // 不能等 state 更新后再读。确认 / 拒绝 / 上传后的刷新走 reloadList，读最新 ref。
    const handleSearch = () => {
        const newPg = { ...paginationRef.current, current: 1 };
        setPagination(newPg);
        fetchData(newPg, filtersRef.current);
    };
    const applyFilter = (patch) => {
        const nextFilters = { ...filtersRef.current, ...patch };
        const newPg = { ...paginationRef.current, current: 1 };
        setFilters(nextFilters);
        setPagination(newPg);
        fetchData(newPg, nextFilters);
    };
    const handleReset = () => {
        const defaults = { status: 'pending_review_all', flight_number: '', aircraft_reg: '', dateRange: null, source_type: undefined };
        const newPg = { current: 1, pageSize: 20 };
        setFilters(defaults);
        setPagination(newPg);
        fetchData(newPg, defaults);
    };
    const handleTableChange = (pag) => {
        const newPg = { current: pag.current, pageSize: pag.pageSize };
        setPagination(newPg);
        fetchData(newPg, filtersRef.current);
    };

    const handleQuickReject = (record) => {
        let reason = '';
        Modal.confirm({
            title: '快速拒绝',
            icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
            content: <Input.TextArea rows={3} placeholder="拒绝原因（必填）" onChange={(e) => { reason = e.target.value; }} />,
            okText: '拒绝', okButtonProps: { danger: true }, cancelText: '取消',
            onOk: async () => {
                if (!reason.trim()) {
                    message.warning('请输入原因');
                    return Promise.reject();
                }
                try {
                    await fuelFormImageApi.reject(record.id, reason.trim());
                    message.success('已拒绝');
                    reloadList();
                } catch (err) {
                    message.error('拒绝失败: ' + (err.response?.data?.detail?.message || err.message));
                }
            },
        });
    };

    const handleRecover = async (record) => {
        try {
            await fuelFormImageApi.recover(record.id);
            message.success('已恢复审核');
            reloadList();
        } catch (err) {
            message.error('恢复失败: ' + (err.response?.data?.detail?.message || err.message));
        }
    };

    // T44: 重新识别 — 仅对 failed_permanent 行有效。需要二次确认，
    // 因为会消耗一次 VLM 调用配额（避免误点）。
    const handleReExtract = (record) => {
        Modal.confirm({
            title: '重新识别',
            icon: <ExclamationCircleOutlined style={{ color: '#1677ff' }} />,
            content: '将再次调用 AI 识别这张图片（消耗一次配额）。如果原因是图片本身模糊，建议先「拒绝」并重新上传更清晰的图片。',
            okText: '确定重新识别', cancelText: '取消',
            onOk: async () => {
                try {
                    await fuelFormImageApi.reExtract(record.id);
                    message.success('已加入识别队列，请稍候刷新');
                    reloadList();
                } catch (err) {
                    message.error('重新识别失败: ' + (err.response?.data?.detail?.message || err.message));
                }
            },
        });
    };

    // T44 批量版：选中行里只有 failed_permanent 会被重抽，其余由后端跳过并计数。
    // 选中的行可能跨页，预筛不可靠，所以整批提交、以返回计数为准。
    // 后端上限 100 条，前端先拦，避免一次烧掉大量配额或请求超时。
    const handleBatchReExtract = () => {
        const ids = [...selectedRowKeys];
        if (!ids.length) return;
        if (ids.length > 100) {
            message.warning(`一次最多重抽 100 张，当前已选 ${ids.length} 张，请分批操作`);
            return;
        }
        Modal.confirm({
            title: '批量重新识别',
            icon: <ExclamationCircleOutlined style={{ color: '#1677ff' }} />,
            content: `将对选中的 ${ids.length} 张图片再次调用 AI 识别（仅「识别失败」的记录会被重新排队，每张消耗一次配额）。`,
            okText: '确定重新识别', cancelText: '取消',
            onOk: async () => {
                try {
                    const res = await fuelFormImageApi.reExtractBatch(ids);
                    const r = res.data || res;
                    const parts = [`已重新排队 ${r.requeued} 张`];
                    if (r.skipped) parts.push(`跳过 ${r.skipped} 张（仅识别失败可重抽）`);
                    if (r.not_found) parts.push(`${r.not_found} 张未找到`);
                    if (r.requeued > 0) {
                        message.success(parts.join('，'));
                    } else {
                        message.warning(parts.join('，'));
                    }
                    setSelectedRowKeys([]);
                    reloadList();
                } catch (err) {
                    message.error('批量重新识别失败: ' + (err.response?.data?.detail?.message || err.message));
                }
            },
        });
    };

    const columns = [
        {
            title: '状态', dataIndex: 'status', width: 160, fixed: 'left',
            render: (v) => <StatusTag status={v} />,
        },
        {
            title: '来源', dataIndex: 'source_type', width: 90, align: 'center',
            render: (v) => <SourceTag sourceType={v} />,
        },
        {
            title: '文件', dataIndex: 'file_name', ellipsis: true, width: 200,
            render: (v, r) => (
                <Space size={4}>
                    <PreviewThumb imageId={r.id} />
                    <Text ellipsis style={{ maxWidth: 150 }}>{v}</Text>
                </Space>
            ),
        },
        { title: '航班号', dataIndex: 'flight_number', width: 90, render: (v) => v || <Text type="secondary">-</Text> },
        { title: '日期', dataIndex: 'flight_date', width: 100, render: (v) => v || '-' },
        { title: '机号', dataIndex: 'aircraft_reg', width: 90, render: (v) => v || '-' },
        {
            title: '置信度', dataIndex: 'extraction_confidence', width: 75, align: 'center',
            render: (v, r) => <ConfidenceTag value={v} status={r.status} />,
        },
        {
            title: '绑定航班', dataIndex: 'flight_operation', width: 250, ellipsis: true,
            render: (fo, r) => {
                const matchTag = MATCH_STATUS_TAG[r.flight_match_status] || MATCH_STATUS_TAG.unmatched;
                if (!r.flight_operation_id) {
                    // 待审核多候选用状态列体现；这里只标"未匹配"
                    return <Tag color={matchTag.color} style={{ margin: 0 }}>{matchTag.label}</Tag>;
                }
                return (
                    <Space size={4} wrap>
                        <Tag color={matchTag.color} style={{ margin: 0 }}>{matchTag.label}</Tag>
                        <Text style={{ fontFamily: 'Courier New, monospace', fontSize: 12 }}>
                            {fo
                                ? `${fo.flight_number || '-'} · ${fo.flight_date || '-'} · ${fo.departure_airport || '?'}→${fo.arrival_airport || '?'}`
                                : `#${r.flight_operation_id}`}
                        </Text>
                        {fo?.deleted && <Tag color="red" style={{ margin: 0 }}>航班已删除</Tag>}
                    </Space>
                );
            },
        },
        {
            title: '上传时间', dataIndex: 'uploaded_at', width: 130,
            render: (v) => <Text style={{ fontFamily: 'Courier New, monospace', fontSize: 12 }}>
                {v ? dayjs(v).format('MM-DD HH:mm') : '-'}
            </Text>,
        },
        {
            title: '操作', width: 200, fixed: 'right',
            render: (_, r) => (
                <Space size={4}>
                    <Tooltip title="查看 / 审核">
                        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setReviewImageId(r.id)}>
                            查看
                        </Button>
                    </Tooltip>
                    {hasEditPermission && r.status === 'rejected' && (
                        <Button type="link" size="small" icon={<ReloadOutlined />} onClick={() => handleRecover(r)}>
                            恢复
                        </Button>
                    )}
                    {hasEditPermission && r.status === 'failed_permanent' && (
                        <Button type="link" size="small" icon={<ReloadOutlined />} onClick={() => handleReExtract(r)}>
                            重新识别
                        </Button>
                    )}
                    {hasEditPermission && [
                        'pending_review', 'pending_review_low_confidence', 'pending_review_ambiguous_match', 'failed_permanent',
                    ].includes(r.status) && (
                        <Button type="link" size="small" danger onClick={() => handleQuickReject(r)}>
                            拒绝
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div>
            {hasEditPermission && (
                <Card size="small" style={{ marginBottom: 12, borderRadius: 8 }}>
                    <FuelFormImageUploader onUploaded={reloadList} />
                </Card>
            )}

            <Card size="small" style={{ marginBottom: 12, borderRadius: 8 }} styles={{ body: { padding: '12px 16px' } }}>
                <Row gutter={[12, 8]} align="middle">
                    <Col xs={24} md={5}>
                        <Text type="secondary" style={{ fontSize: 12 }}>状态</Text>
                        <Select
                            size="small"
                            style={{ width: '100%' }}
                            value={filters.status}
                            onChange={(v) => applyFilter({ status: v })}
                            options={STATUS_FILTER_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                            allowClear
                        />
                    </Col>
                    <Col xs={12} md={3}>
                        <Text type="secondary" style={{ fontSize: 12 }}>来源</Text>
                        <Select
                            size="small"
                            style={{ width: '100%' }}
                            placeholder="全部"
                            value={filters.source_type}
                            onChange={(v) => applyFilter({ source_type: v })}
                            options={SOURCE_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                            allowClear
                        />
                    </Col>
                    <Col xs={12} md={3}>
                        <Text type="secondary" style={{ fontSize: 12 }}>航班号（模糊）</Text>
                        <Input size="small" placeholder="如 CT166 / 166" allowClear
                            value={filters.flight_number} onPressEnter={handleSearch}
                            onChange={(e) => setFilters((f) => ({ ...f, flight_number: e.target.value }))} />
                    </Col>
                    <Col xs={12} md={3}>
                        <Text type="secondary" style={{ fontSize: 12 }}>机号（模糊，逗号可多选）</Text>
                        <Input size="small" placeholder="如 9H-WFF / WFF" allowClear
                            value={filters.aircraft_reg} onPressEnter={handleSearch}
                            onChange={(e) => setFilters((f) => ({ ...f, aircraft_reg: e.target.value }))} />
                    </Col>
                    <Col xs={24} md={6}>
                        <Text type="secondary" style={{ fontSize: 12 }}>识别航班日期</Text>
                        <RangePicker size="small" style={{ width: '100%' }} value={filters.dateRange}
                            onChange={(v) => applyFilter({ dateRange: v })} />
                    </Col>
                    <Col xs={24} md={4} style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <Space>
                            <Button size="small" type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
                            <Button size="small" icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <div style={{ marginBottom: 8, padding: '6px 12px', background: '#fafafa', borderRadius: 4, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Space size={20}>
                    <span><Text strong>{total}</Text> 条记录</span>
                    <span><CheckCircleOutlined style={{ color: '#52c41a' }} /> 已确认 <Text strong>{data.filter((d) => d.status === 'confirmed').length}</Text></span>
                    <span><ClockCircleOutlined style={{ color: '#faad14' }} /> 待审核 <Text strong>{data.filter((d) => String(d.status).startsWith('pending_review')).length}</Text></span>
                </Space>
                {selectedRowKeys.length > 0 && hasEditPermission && (
                    <Space>
                        <Text type="secondary">已选 {selectedRowKeys.length} 张</Text>
                        <Button size="small" icon={<ReloadOutlined />} onClick={handleBatchReExtract}>批量重新识别</Button>
                        <Button size="small" type="link" onClick={() => setSelectedRowKeys([])}>取消选择</Button>
                    </Space>
                )}
            </div>

            <Table
                columns={columns}
                dataSource={data}
                rowKey="id"
                size="small"
                bordered
                loading={loading}
                scroll={{ x: 1200 }}
                rowSelection={hasEditPermission ? {
                    selectedRowKeys,
                    onChange: setSelectedRowKeys,
                    columnWidth: 40,
                } : undefined}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total,
                    showSizeChanger: true,
                    showTotal: (t) => `共 ${t} 条`,
                    pageSizeOptions: ['10', '20', '50'],
                }}
                onChange={handleTableChange}
            />

            <FuelFormImageReviewDrawer
                imageId={reviewImageId}
                open={!!reviewImageId}
                onClose={() => { setReviewImageId(null); clearDetailIdParam(); }}
                onChanged={reloadList}
                hasEditPermission={hasEditPermission}
            />
        </div>
    );
};

export default FuelFormImageList;
